import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import RadarIcon from '@mui/icons-material/Radar';
import SortIcon from '@mui/icons-material/Sort';
import CategoryIcon from '@mui/icons-material/Category';
import { Category, SortBy } from '../types';

const RADII = [2, 5, 10, 15, 20] as const;
const CATEGORIES: Category[] = [
  'Buffet', 'Kotiruoka', 'Thai', 'Pizza', 'Grilli', 'Kasvis', 'Kala', 'Keitto',
];

interface SearchFiltersProps {
  query: string;
  onQueryChange: (q: string) => void;
  radiusKm: 2 | 5 | 10 | 15 | 20;
  onRadiusChange: (r: 2 | 5 | 10 | 15 | 20) => void;
  selectedCategories: Category[];
  onCategoriesChange: (c: Category[]) => void;
  sortBy: SortBy;
  onSortChange: (s: SortBy) => void;
}

export default function SearchFilters({
  query,
  onQueryChange,
  radiusKm,
  onRadiusChange,
  selectedCategories,
  onCategoriesChange,
  sortBy,
  onSortChange,
}: SearchFiltersProps) {
  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onCategoriesChange(
      (typeof value === 'string' ? value.split(',') : value) as Category[]
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(232,245,233,0.5) 100%)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        {/* Text search */}
        <TextField
          size="small"
          placeholder="Hae ravintolaa..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Radius selector */}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RadarIcon sx={{ fontSize: 16 }} />
              Säde
            </Box>
          </InputLabel>
          <Select
            value={radiusKm}
            label="⦿ Säde"
            onChange={(e) => onRadiusChange(Number(e.target.value) as typeof radiusKm)}
          >
            {RADII.map((r) => (
              <MenuItem key={r} value={r}>
                {r} km
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Category multi-select */}
        <FormControl size="small" sx={{ minWidth: 160, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
          <InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CategoryIcon sx={{ fontSize: 16 }} />
              Kategoria
            </Box>
          </InputLabel>
          <Select
            multiple
            value={selectedCategories}
            onChange={handleCategoryChange}
            input={<OutlinedInput label="⦿ Kategoria" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            )}
          >
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort selector */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SortIcon sx={{ fontSize: 16 }} />
              Järjestys
            </Box>
          </InputLabel>
          <Select
            value={sortBy}
            label="⦿ Järjestys"
            onChange={(e) => onSortChange(e.target.value as SortBy)}
          >
            <MenuItem value="distance">Etäisyys</MenuItem>
            <MenuItem value="price">Hinta</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );
}
